import React, { useEffect, useRef } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { OfferFormValues } from "../../../schemas/offerFormSchema";
import { FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Variation } from "@/components/admin/locations/types/variation";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SessionAgeGroupOverrideFields } from "@/components/admin/locations/components/shared/SessionAgeGroupOverrideFields";

interface Props {
  form: UseFormReturn<OfferFormValues>;
  variation: Variation | null | undefined;
  allowOverride: boolean;
  resetFieldToProgram: (fieldName: string) => void;
}

export const SessionAgeOverrideSection: React.FC<Props> = ({ form, variation, allowOverride }) => {
  const overrideChecked = !!useWatch({ control: form.control, name: "overrideAges" });

  // Seed once per variation when override is enabled to avoid render loops
  const seededForVariationKeyRef = useRef<string | null>(null);
  const variationKey = (variation as any)?.key as string | undefined;

  useEffect(() => {
    if (!allowOverride || !variation) return;
    if (!overrideChecked) {
      // Reset marker so re-enabling will seed again
      seededForVariationKeyRef.current = null;
      return;
    }

    // Only seed once per variation selection
    if (seededForVariationKeyRef.current === variationKey) return;

    const setIfEmpty = (name: keyof OfferFormValues, value: any) => {
      const current = form.getValues(name as any);
      if (current === undefined || current === null || current === "") {
        form.setValue(name as any, value, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
      }
    };

    setIfEmpty("minAgeInYearsOverride", (variation.minAgeInYears ?? null) as any);
    setIfEmpty("maxAgeInYearsOverride", (variation.maxAgeInYears ?? null) as any);
    setIfEmpty("minAgeGradeKeyOverride", (variation.minAgeGradeKey || "") as any);
    setIfEmpty("maxAgeGradeKeyOverride", (variation.maxAgeGradeKey || "") as any);
    setIfEmpty("minAgeNoteOverride", "");
    setIfEmpty("maxAgeNoteOverride", "");
    setIfEmpty("showMinAgeGradeAsPrimaryOverride", !!variation.showMinAgeGradeAsPrimary as any);
    setIfEmpty("showMaxAgeGradeAsPrimaryOverride", !!variation.showMaxAgeGradeAsPrimary as any);

    // Mark as seeded for this variation key
    seededForVariationKeyRef.current = variationKey ?? "__unknown__";
  }, [allowOverride, overrideChecked, variation, variationKey, form]);

  if (!allowOverride) return null;

  return (
    <div className="space-y-4 rounded-md bg-muted p-4">
      <FormField
        control={form.control}
        name="overrideAges"
        render={({ field }) => (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={!!field.value}
              onChange={e => field.onChange(e.target.checked)}
            />
            <span>Override program age range</span>
          </label>
        )}
      />

      {overrideChecked && (
        <SessionAgeGroupOverrideFields form={form} />
      )}
    </div>
  );
};


